import { useCallback, useEffect, useLayoutEffect, useRef } from "react"
import { WELCOME_MOTION as T } from "./welcomeMotion"
import { renderWelcomeSound } from "./welcomeSound"

const MASTER_PEAK = 0.22

/**
 * Plays the welcome bloom on the click clock, its opening exactly on the
 * flash. No context exists until play() is called synchronously from the
 * start click, so nothing can ever sound before the user acted.
 */
export const useWelcomeSound = (open = true) => {
  const session = useRef<{
    ctx: AudioContext
    cancelled: boolean
    timer?: ReturnType<typeof setTimeout>
  } | null>(null)
  const activated = useRef(false)

  const cancel = useCallback(() => {
    const current = session.current
    if (!current) return
    current.cancelled = true
    clearTimeout(current.timer)
    try {
      void current.ctx.close().catch(() => {})
    } catch {
      // Unsupported/failed audio must never interrupt the welcome.
    }
    session.current = null
  }, [])

  useEffect(() => cancel, [cancel])
  useLayoutEffect(() => {
    if (open) activated.current = false
    else cancel()
  }, [open, cancel])

  const play = useCallback(() => {
    if (activated.current) return
    activated.current = true
    try {
      const ctx = new AudioContext()
      const current = { ctx, cancelled: false } as NonNullable<
        typeof session.current
      >
      session.current = current
      const schedule = () => {
        if (current.cancelled) return
        try {
          const t0 = ctx.currentTime
          const master = ctx.createGain()
          master.gain.value = MASTER_PEAK
          master.connect(ctx.destination)
          renderWelcomeSound(ctx, master, t0)

          current.timer = setTimeout(cancel, T.audioStop * 1000)
        } catch {
          cancel()
        }
      }
      if (ctx.state === "suspended") {
        void ctx.resume().then(schedule).catch(cancel)
      } else {
        schedule()
      }
    } catch {
      cancel()
    }
  }, [cancel])

  return { play, cancel }
}
